import React, { useState } from "react";

import emailValidator from "email-validator";
import {
  compact,
  find,
  isEmpty,
  map,
  filter,
  difference,
  some,
} from "lodash/fp";

import useSendTopicReport from "@app/analytics/TopicReport/hooks/useSendTopicReport";
import {
  ContentContainer,
  Footer,
} from "@app/common/access/ShareReport/styles";
import { toast } from "@app/common/hooks/useToasts";
import LINKS from "@app/common/links";
import type { ShareEntity } from "@app/common/types";
import { isEmailProvider } from "@app/common/utils/providerUtils";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import AddShareEntitiesInput, { Option } from "@rui/AddShareEntitiesInput";
import Alert from "@rui/Alert";
import Link from "@rui/Link";
import ShareSuggestions, { Suggestion } from "@rui/ShareSuggestions";
import Stack, { Row } from "@rui/Stack";
import { Bold } from "@rui/styles";
import TooltipWrapper from "@rui/TooltipWrapper";

import AddAMessage from "./AddAMessage";
import { FooterButton } from "./styles";
import type { DialogContentProps } from "./types";

const getEmailsFromEntities = (options: Option[]) =>
  compact(map((o) => (typeof o === "string" ? o : o.email), options));

const SendViaEmailContent = ({
  contributors,
  onClose,
  selectedEntityKinds,
  topicId,
}: DialogContentProps) => {
  const [message, setMessage] = useState("");
  const [selectedEntities, setSelectedEntities] = useState<ShareEntity[]>([]);
  const { workspaceId } = useWorkspace();

  const { shareTopicReportViaEmail } = useSendTopicReport();

  const handleSelectSuggestion = (suggestion: Suggestion) => {
    setSelectedEntities((state) => [...state, suggestion as ShareEntity]);
  };

  const suggestions = compact(
    contributors.map((contributor) => {
      if (!isEmailProvider(contributor.provider)) return null;

      if (
        find({ email: contributor.displayName }, selectedEntities) ||
        selectedEntities.includes(contributor.displayName)
      ) {
        return null;
      }

      return {
        id: contributor.providerUserId,
        name: contributor.displayName,
      };
    })
  );

  const handleClickDone = () => {
    const emails = getEmailsFromEntities(selectedEntities);

    shareTopicReportViaEmail.mutate(
      {
        emails,
        includeEntities: selectedEntityKinds,
        message,
        topicId,
      },
      {
        onSuccess: () => {
          onClose();
          toast.success("Successfully sent your Readout!");
        },
      }
    );
  };

  const hasInvalidEmails = some(
    (e) => !emailValidator.validate(e),
    getEmailsFromEntities(selectedEntities)
  );
  const submitDisabled = isEmpty(selectedEntities) || hasInvalidEmails;
  const additionalSuggestedOptions = map("name", suggestions);

  const getOptions = (inputValue: string, options: Option[]) => {
    // filter out any options without an email, since we need an email in order to share via email
    const validOptions = filter(
      (o) => typeof o === "string" || o.email != null,
      options
    );

    const optionEmails = getEmailsFromEntities(validOptions);

    const additionalCollaborators = difference(
      additionalSuggestedOptions,
      optionEmails
    );

    const matchingCollaborators = inputValue
      ? filter(
          (email) => email.indexOf(inputValue) > -1,
          additionalCollaborators
        )
      : [];

    // inject collaborators that 1) are not already included in the
    // workspace search results and 2) match the input value
    return [...validOptions, ...matchingCollaborators];
  };

  return (
    <>
      <ContentContainer>
        <Stack paddingY={2} spacing={2}>
          <Stack spacing={1}>
            <AddShareEntitiesInput
              getOptions={getOptions}
              onChange={setSelectedEntities}
              selectedEntities={selectedEntities}
              workspaceSearchParams={{ workspaceId, includeEntities: ["user"] }}
            />
            <ShareSuggestions
              onSelectSuggestion={handleSelectSuggestion}
              suggestions={suggestions}
            />
            <AddAMessage onChange={setMessage} />
            {shareTopicReportViaEmail.isError && (
              <Alert
                severity="error"
                embedded
                alignItems="flex-start"
                sx={{ border: "1px solid #D32F2F" }}
              >
                <Bold>Error</Bold>
                <br />
                There was an error sending your report. Please try again or
                contact <Link href={LINKS.EMAIL.SUPPORT}>
                  support@read.ai
                </Link>{" "}
                if the problem persists.
              </Alert>
            )}
          </Stack>
        </Stack>
      </ContentContainer>
      <Footer>
        <Row variant="right" flex={1}>
          <TooltipWrapper
            show={submitDisabled}
            placement="top"
            title={
              hasInvalidEmails
                ? "Please ensure all emails are valid"
                : "Please enter one or more emails"
            }
          >
            <FooterButton
              variant="outlined"
              onClick={handleClickDone}
              loading={shareTopicReportViaEmail.isPending}
              disabled={submitDisabled}
            >
              Send report
            </FooterButton>
          </TooltipWrapper>
        </Row>
      </Footer>
    </>
  );
};

export default SendViaEmailContent;
